import { Injectable } from "@angular/core";

import { chartsData } from "../charts/charts-data.interface";
import { StrategyBaseChartsService } from "./strategy-base-charts.service";
import { ChartsService } from "../charts/charts.service";
import { StrategyCommonService } from "./strategy-common.service";
import { StrategyDataParserService } from "./strategy-data-parser.service";

import {
  HsEventBusService,
  HsLanguageService,
  HsUtilsService,
} from "hslayers-ng";
import { forkJoin, Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class StrategyClimaService {
  fetchedData = [];
  clima;
  climaDiagramData;
  factors = ["preci", "tavg"];
  definitions = {
    charts: {
      month: [
        {
          data: this.factors,
          mark: ["bar", "line"],
          title: "clima",
          unit: ["mm", "°C"],
          layered: true,
        },
      ],
    },
  };

  dataFrequency = "month";
  createClimaDiagram$ = new Subject();

  constructor(
    public StrategyBaseChartsService: StrategyBaseChartsService,
    public HsEventBusService: HsEventBusService,
    public HsUtilsService: HsUtilsService,
    public HsLanguageService: HsLanguageService,
    public ChartsService: ChartsService,
    public StrategyCommonService: StrategyCommonService,
    public parserService: StrategyDataParserService
  ) {
    this.StrategyCommonService.fetchData$.subscribe((coords) => {
      this.fetchClimaDiagramData(coords);
    });
    this.StrategyCommonService.renderCharts$.subscribe((coords) => {
      this.renderClimaDiagram();
    });
  }

  renderClimaDiagram() {
    this.climaDiagramData = <chartsData>{
      datasets: { week_data: this.clima },
      definitions: this.definitions,
      dataFrequency: this.dataFrequency,
      uncertainity: this.StrategyCommonService.uncertainity,
      componentChartOverrides: this.climaDiagramOverrides,
      chartContainerClass: ".ac-chartplace",
      columns: 1,
      overview: this.StrategyCommonService.overview,
      chartHeight: 0.18,
    };
  }

  fetchClimaDiagramData(coords) {
    this.StrategyCommonService.climaDataLoading =
      this.StrategyCommonService.dataFrequency == "week" &&
      this.StrategyCommonService.overview == "place";
    if (!this.StrategyCommonService.climaDataLoading) return;
    const observables = [];

    for (const factor of this.factors) {
      let url = [
        this.StrategyCommonService.apiUrl,
        this.dataFrequency,
        factor,
        "0", //crop
        coords[1],
        coords[0],
        "0.1", // neighborhood
        this.StrategyCommonService.selectedDate.upper, // year from
        this.StrategyCommonService.selectedDate.upper + 1, // year to
        "1", // from month
        "12", // to month
        "ERA5-Land", //datasoruce
      ]
        .filter(Boolean)
        .join("/");
      const promise = this.StrategyCommonService.makeClimaticDataRequest(
        this.HsUtilsService.proxify(url),
        factor,
        this.fetchedData
      );
      observables.push(promise);
    }

    forkJoin(observables).subscribe((data: Array<any>) => {
      if (data.every((observable) => observable.error)) {
        this.StrategyCommonService.handleFetchError(data);
      } else {
        this.clima = this.aggregateByMonth();
        this.StrategyCommonService.climaDataLoading = false;
      }
    });
  }

  aggregateByMonth() {
    let dateRange = this.StrategyCommonService.createDateRangeObject(
      this.dataFrequency,
      this.StrategyCommonService.selectedDate.upper,
      this.StrategyCommonService.selectedDate.upper + 1
    );
    for (const [index, date] of dateRange.entries()) {
      const keyModifier = this.dataFrequency !== "year" ? `_${date.month}` : "";
      // ... find corresponding and modify
      this.parserService.parseFetchedDataValues(
        this.fetchedData,
        date,
        keyModifier
      );
      date.month = new Date(date.year, date.month, 1)
        .toISOString()
        .split("T")[0];
    }
    return dateRange;
  }

  assignFactorValues(dateRange, formatedObject, factor) {
    const chunkSize = factor == "preci" ? 4 : 31;
    for (let [key, value] of Object.entries(formatedObject)) {
      const divider = factor == "preci" && key == "fac" ? 1 : chunkSize; //Sum or average value
      this.createArrayChunks(formatedObject[key], chunkSize, key).map(
        (month, index) => {
          //Calculate sum of factor
          if (dateRange[index]) {
            dateRange[index][key == "fac" ? factor : `${factor}${key}`] =
              <number>(
                month.reduce(
                  (a, b) => parseFloat(a as string) + parseFloat(b as string),
                  0
                )
              ) / divider;
          }
        }
      );
    }
  }

  /**
   * Creates chunk of **size** which can then be aggregated into monthly sum/average
   * Size 4 || 31 (weekly || dayly data)
   */
  createArrayChunks(obj, size, valueType) {
    const filteredArray = Object.entries(obj).filter(([key, val]) => true);
    return [...Array(Math.ceil(filteredArray.length / size))].map((_) =>
      filteredArray
        .splice(0, size)
        .flat()
        .filter((e) => !(e as string).includes(valueType))
    );
  }

  climaDiagramOverrides(options, chartTemplate, dimensions, langService) {
    if (options.uncertainity) {
      chartTemplate.layer.splice(2, 0, chartTemplate.layer.splice(1, 1)[0]);
    }

    chartTemplate.resolve = { scale: { y: "independent" } };
    chartTemplate.layer[0].encoding.color.value = "#76B9E5";
    chartTemplate.layer[0].encoding.y.scale = {
      domain: { unionWith: [-20, 200] },
      zero: true,
    };
    chartTemplate.layer[0].encoding.tooltip[0].axis.format = "%B";
    chartTemplate.layer[0].encoding.tooltip.push({
      field: "tavg",
      type: "quantitative",
      title: langService.getTranslationIgnoreNonExisting(
        "CHARTS.title",
        "tavg"
      ),
    });

    chartTemplate.layer[1].encoding.y.scale = {
      domain: { unionWith: [-10, 100] },
      zero: true,
    };

    chartTemplate.encoding = {};
    chartTemplate.encoding.x = { ...chartTemplate.layer[0].encoding.x };
    chartTemplate.encoding.x.type = "ordinal";
    chartTemplate.encoding.x.axis.format = "%b";
    chartTemplate.encoding.x.axis.labelAngle = 0;
    chartTemplate.encoding.x.ticks = false;
    for (let layer of chartTemplate.layer) {
      delete layer.encoding.x;
    }
    chartTemplate.layer[1].view = { stroke: "red" };
    delete chartTemplate.layer[1].selection;
    delete chartTemplate.layer[1].encoding.color;
    delete chartTemplate.layer[1].encoding.height;
    delete chartTemplate.layer[1].encoding.scale;
    delete chartTemplate.layer[1].encoding.stack;
    Object.assign(chartTemplate.layer[1].encoding.y.axis, {
      titleColor: "red",
      titleAngle: -90,
      titlePadding: 15,
    });
    chartTemplate.layer[1].mark.color = "red";
    if (options.uncertainity) {
      chartTemplate.layer[2].encoding.y.axis = null;
      chartTemplate.layer[3].encoding.y.axis = null;
      chartTemplate.layer[2].encoding.y.scale = {
        domain: { unionWith: [-20, 200] },
        zero: true,
      };
      chartTemplate.layer[3].encoding.y.scale = {
        domain: { unionWith: [-10, 100] },
        zero: true,
      };
    }
  }
}
